import React, { useState, useEffect, Suspense } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, TrackballControls } from '@react-three/drei';
import { GLTFLoader } from 'three-stdlib';
import { Object3D } from 'three';
import { Box3, Vector3 } from 'three';

import axios from 'axios';
import config from '../config';


export function base64ToArrayBuffer(base64: string): ArrayBuffer {
    // Elimina prefijo data URL si existe y espacios
    var cleaned = base64
        .replace(/^data:.*;base64,/, '')
        .replace(/\s/g, '')
        .replace(/\\\//g, '/') // desescapa barras inclinadas
        .replace(/\\/g, "\\"); // desescapa doble barra invertida

    // Elimina cualquier caracter no base64 (solo A-Za-z0-9+/=)
    cleaned = cleaned.replace(/[^A-Za-z0-9+/=]/g, '');

    // Log para depuración
    console.log('Base64 cleaned:', cleaned.slice(0, 100), '...');
    try {
        const binary = window.atob(cleaned);
        const len = binary.length;
        const buffer = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
            buffer[i] = binary.charCodeAt(i);
        }
        console.log('ArrayBuffer length:', buffer.buffer.byteLength);
        return buffer.buffer;
    } catch (e) {
        console.error('Error decoding base64:', e, cleaned.slice(0, 100));
        throw e;
    }
}

const GLBFromBase64: React.FC<{ base64: string | null; onFit?: (scene: Object3D) => void }> = ({ base64, onFit }) => {
    const [scene, setScene] = useState<Object3D | null>(null);

    useEffect(() => {
        if (!base64) {
            setScene(null);
            return;
        }
        try {
            const arrayBuffer = base64ToArrayBuffer(base64);
            const loader = new GLTFLoader();
            loader.parse(arrayBuffer, '', (gltf) => {
                gltf.scene.scale.set(100, 100, 100);
                gltf.scene.traverse((child: any) => {
                    if (child.isMesh && child.material) {
                        if (child.material.metalness !== undefined) child.material.metalness = 0.6;
                        if (child.material.roughness !== undefined) child.material.roughness = 0.3;
                        if (child.material.reflectivity !== undefined) child.material.reflectivity = 0.5;
                        child.material.needsUpdate = true;
                    }
                });
                setScene(gltf.scene);
                if (onFit) onFit(gltf.scene);
            }, (err) => {
                setScene(null);
            });
        } catch (e) {
            setScene(null);
        }
    }, [base64, onFit]);

    if (!scene) return null;
    return <primitive object={scene} />;
};


// Configuración global de axios para incluir credenciales
axios.defaults.withCredentials = true;

const primary = '#8d3939ff';

const RepresentationSelectorModal: React.FC<{
    onClose: () => void;
    onSelect: (element: any) => void;
}> = ({ onClose, onSelect }) => {
    const [categories, setCategories] = useState<string[]>([]);
    const [subCategories, setSubCategories] = useState<string[]>([]);
    const [elements, setElements] = useState<{ id: number; name: string }[]>([]);

    const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
    const [selectedSubCategory, setSelectedSubCategory] = useState<string | null>(null);
    const [selectedElement, setSelectedElement] = useState<{ id: number; name: string } | null>(null);
    const [componentInfo, setComponentInfo] = useState<{ id: number; name: string; content: string; hash?: string } | null>(null);
    const [selected, setSelected] = useState<any>(null);

    // Fetch categories on mount
    useEffect(() => {
        axios.post(`${config.API_BASE_URL}/representation/get-unique-categories.php`)
            .then(response => {
                if (response.data.success) {
                    const sortedCategories = response.data.categories.sort((a: string, b: string) => a.localeCompare(b));
                    setCategories(sortedCategories);
                }
            })
            .catch(error => console.error('Error fetching categories:', error));
    }, []);

    // Fetch subcategories when a category is selected
    useEffect(() => {
        if (selectedCategory) {
            axios.post(`${config.API_BASE_URL}/representation/get-subcategories-by-category.php`, { category: selectedCategory })
                .then(response => {
                    if (response.data.success) {
                        const sortedSubCategories = response.data.sub_categories.sort((a: string, b: string) => a.localeCompare(b));
                        setSubCategories(sortedSubCategories);
                        setElements([]); // Clear elements when category changes
                        setSelectedSubCategory(null);
                        setSelected(null); // Clear the selected component
                        setComponentInfo(null);
                    }
                })
                .catch(error => console.error('Error fetching subcategories:', error));
        }
    }, [selectedCategory]);

    // Fetch elements when a subcategory is selected
    useEffect(() => {
        if (selectedCategory && selectedSubCategory) {
            console.log('Selected Category:', selectedCategory);
            axios.post(`${config.API_BASE_URL}/representation/get-elements-by-category-and-subcategory.php`, {
                category: selectedCategory,
                sub_category: selectedSubCategory
            })
                .then(response => {
                    if (response.data.success) {
                        console.log('Response:');
                        const sortedElements = response.data.elements.sort((a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name));
                        setElements(sortedElements);
                        setSelected(null); // Clear the selected component
                        setSelectedElement(null);
                        setComponentInfo(null);
                    }
                })
                .catch(error => console.error('Error fetching elements:', error));
        }
    }, [selectedCategory, selectedSubCategory]);

    const handleElementClick = (element: { id: number; name: string }) => {
        setSelectedElement(element);
        axios.post(`${config.API_BASE_URL}/representation/get-representation.php`, { id: element.id })
            .then(response => {
                if (response.data.success) {
                    console.log('Component data:', base64ToArrayBuffer(response.data.data.component));
                    setComponentInfo({ id: element.id, name: element.name, content: response.data.data.component, hash: response.data.data.hash });
                    setSelected(response.data.data.description);
                }
            })
            .catch(error => console.error('Error fetching component and display_table:', error));
    };

    return (
        <div style={{
            position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
            background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
            <div style={{
                background: '#f9f9f9', width: 860, maxWidth: '96%', height: 560, borderRadius: 12, display: 'flex', flexDirection: 'column',
                boxShadow: '0 14px 40px rgba(13,21,28,0.24)', overflow: 'hidden', fontFamily: 'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial'
            }}>
                <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
                    <div style={{ width: 360, padding: 20, display: 'flex', flexDirection: 'column', gap: 18, borderRight: '1px solid #f0f0f0' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <label style={{ color: '#666', fontSize: 13, minWidth: 70 }}>Library</label>
                            <select
                                value={selectedCategory || ''}
                                onChange={e => setSelectedCategory(e.target.value)}
                                style={{ flex: 1, padding: '10px 12px', borderRadius: 8, border: '1px solid #e9e9e9', background: '#fff', fontSize: 14 }}
                            >
                                <option value="" disabled>Please select a category</option>
                                {categories.map(category => (
                                    <option key={category} value={category}>{category}</option>
                                ))}
                            </select>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            <div style={{ color: '#666', fontSize: 13 }}>Category</div>
                            <ul style={{ listStyle: 'none', padding: 6, margin: 0, maxHeight: 150, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 ,  background: '#ffffff'}}>
                                {subCategories.map(subCategory => (
                                    <li
                                        key={subCategory}
                                        style={{
                                            padding: '3px 12px',
                                            cursor: 'pointer',
                                            borderRadius: 8,
                                            border: selectedSubCategory === subCategory ? `1px solid ${primary}` : '1px solid transparent',
                                            background: selectedSubCategory === subCategory ? 'rgba(141,57,57,0.06)' : 'transparent',
                                            color: '#222',
                                            fontSize: 14
                                        }}
                                        onClick={() => setSelectedSubCategory(subCategory)}
                                    >
                                        {subCategory}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            <div style={{ color: '#666', fontSize: 13 }}>Elements</div>
                            <ul style={{ listStyle: 'none', padding: 6, margin: 0, maxHeight: 150, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6,  background: '#ffffff' }}>
                                {elements.map(element => (
                                    <li
                                        key={element.id}
                                        style={{
                                            padding: '3px 12px',
                                            cursor: 'pointer',
                                            borderRadius: 8,
                                            display: 'flex', alignItems: 'center',
                                            justifyContent: 'space-between',
                                            background: selectedElement?.id === element.id ? 'rgba(141,57,57,0.06)' : 'transparent',
                                            border: selectedElement?.id === element.id ? `1px solid ${primary}` : '1px solid transparent',
                                            color: '#222',
                                            fontSize: 14
                                        }}
                                        onClick={() => handleElementClick(element)}
                                    >
                                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{element.name}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    <div style={{ flex: 1, padding: 20, display: 'flex', flexDirection: 'column' }}>
                        <div style={{ flex: 1, borderRadius: 10, background: '#fbfbfb', padding: 12, border: '1px solid #f0f0f0', overflow: 'hidden' }}>
                            {componentInfo?.content ? (
                                <Rescalable3DView base64={componentInfo.content} />
                            ) : (
                                <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888' }}>
                                    <div style={{ textAlign: 'center' }}>
                                        <div style={{ fontSize: 15, marginBottom: 6, color: '#444' }}>Select a 3D model</div>
                                        <div style={{ fontSize: 13 }}>Choose an element on the left to view details.</div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div style={{ borderTop: '1px solid #f3f3f3', padding: 16, display: 'flex', justifyContent: 'flex-end', gap: 12, background: '#fff' }}>
                    <button onClick={onClose} style={{ background: 'transparent', color: '#666', border: '1px solid #e9e9e9', padding: '8px 12px', borderRadius: 8, cursor: 'pointer' }}>Cancel</button>
                    <button
                        onClick={() => componentInfo && onSelect(componentInfo)}
                        disabled={!componentInfo}
                        style={{ background: componentInfo ? primary : '#ddd', color: '#fff', border: 'none', padding: '8px 14px', borderRadius: 8, cursor: componentInfo ? 'pointer' : 'not-allowed' }}
                    >
                        Accept
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- Componente para Canvas con botón de rescalado y ajuste automático ---
const Rescalable3DView: React.FC<{ base64: string | null }> = ({ base64 }) => {
    const controlsRef = React.useRef<any>(null);
    const cameraRef = React.useRef<any>(null);
    const [sceneObj, setSceneObj] = useState<Object3D | null>(null);

    // Función para ajustar la cámara y controles al modelo
    const fitToModel = React.useCallback(() => {
        if (!sceneObj || !cameraRef.current || !controlsRef.current) return;
        // Calcula el bounding box del modelo
        const box = new Box3().setFromObject(sceneObj);
        const size = box.getSize(new Vector3());
        const center = box.getCenter(new Vector3());
        // Ajusta la cámara
        const maxDim = Math.max(size.x, size.y, size.z);
        const fitDist = maxDim * 1.5;
        cameraRef.current.position.set(center.x, center.y, center.z + fitDist);
        cameraRef.current.near = 0.1;
        cameraRef.current.far = fitDist * 10;
        cameraRef.current.updateProjectionMatrix();
        cameraRef.current.lookAt(center);
        // Ajusta los controles
        controlsRef.current.target.copy(center);
        controlsRef.current.update();
    }, [sceneObj]);

    // Ajusta automáticamente al cargar un nuevo modelo
    useEffect(() => {
        if (sceneObj) fitToModel();
    }, [sceneObj, fitToModel]);

    return (
        <div style={{ width: '100%', height: '100%', position: 'relative' }}>
            <button
                style={{ position: 'absolute', zIndex: 10, right: 10, top: 10, background: '#1976d2', color: '#fff', border: 'none', borderRadius: 4, padding: '6px 12px', cursor: 'pointer' }}
                onClick={fitToModel}
                title="Fit View to Model"
            >
                Fit View  
            </button>
            <Canvas
                style={{ width: '100%', height: '100%' }}
                camera={{ position: [0, 0, 200] }}
                onCreated={({ camera }) => { cameraRef.current = camera; }}
            >
                <ambientLight intensity={0.7} />
                <directionalLight position={[5, 10, 7]} intensity={1.2} />
                {base64 && (
                    <GLBFromBase64 base64={base64} onFit={setSceneObj} />
                )}
                <OrbitControls
                    ref={controlsRef}
                    maxPolarAngle={Math.PI}
                    minPolarAngle={0}
                    maxAzimuthAngle={Infinity}
                    minAzimuthAngle={-Infinity}
                    enablePan={true}
                    enableZoom={true}
                    enableRotate={true}
                />
            </Canvas>
        </div>
    );
};

export default RepresentationSelectorModal;